// Categorization helper for mapping detected furniture text to our taxonomy
import { FURNITURE_CATEGORIES } from './categories';

const KEYWORD_MAP = [
  // Living Room seating
  { keys: ['sofa', 'couch', 'chesterfield', 'loveseat'], cat: 'sofas' },
  { keys: ['sectional'], cat: 'sectionals' },
  { keys: ['recliner'], cat: 'recliners' },
  { keys: ['accent chair', 'armchair', 'club chair', 'slipper chair'], cat: 'accent chairs' },
  { keys: ['chaise'], cat: 'chaise lounges' },
  { keys: ['futon', 'sofa bed', 'sleeper'], cat: 'sofa beds' },
  { keys: ['ottoman', 'pouf'], cat: 'ottomans' },
  { keys: ['bench'], cat: 'benches' },
  // Living Room tables/storage
  { keys: ['coffee table', 'cocktail table'], cat: 'coffee tables' },
  { keys: ['end table', 'side table'], cat: 'end tables' },
  { keys: ['console table', 'entry table'], cat: 'console tables' },
  { keys: ['sofa table'], cat: 'sofa tables' },
  { keys: ['tv stand', 'tv console', 'entertainment center', 'media stand'], cat: 'tv stands' },
  { keys: ['media console'], cat: 'media consoles' },
  { keys: ['bookcase', 'book shelf', 'bookshelf'], cat: 'bookcases' },
  { keys: ['shelf', 'shelving'], cat: 'shelving' },
  { keys: ['curio', 'display cabinet'], cat: 'curio cabinets' },
  // Bedroom
  { keys: ['platform bed', 'storage bed', 'panel bed', 'bed frame', 'bed'], cat: 'beds' },
  { keys: ['upholstered bed'], cat: 'upholstered beds' },
  { keys: ['headboard'], cat: 'headboards' },
  { keys: ['mattress', 'memory foam', 'hybrid mattress', 'innerspring'], cat: 'mattresses' },
  { keys: ['nightstand', 'bedside table'], cat: 'nightstands' },
  { keys: ['dresser', 'double dresser', 'triple dresser'], cat: 'dressers' },
  { keys: ['chest', 'chest of drawers'], cat: 'chests' },
  { keys: ['armoire', 'wardrobe'], cat: 'armoires' },
  { keys: ['vanity', 'makeup table'], cat: 'vanities' },
  { keys: ['bunk bed'], cat: 'bunk beds' },
  { keys: ['daybed', 'day bed'], cat: 'daybeds' },
  { keys: ['crib', 'toddler bed'], cat: 'cribs' },
  // Dining
  { keys: ['dining table', 'extendable table', 'pedestal table'], cat: 'dining tables' },
  { keys: ['dining chair', 'side chair', 'arm chair (dining)'], cat: 'dining chairs' },
  { keys: ['bar stool'], cat: 'bar stools' },
  { keys: ['counter stool'], cat: 'counter stools' },
  { keys: ['dining bench'], cat: 'benches (dining)' },
  { keys: ['buffet', 'server'], cat: 'buffets' },
  { keys: ['sideboard'], cat: 'sideboards' },
  { keys: ['china cabinet', 'display hutch'], cat: 'china cabinets' },
  { keys: ['kitchen island'], cat: 'kitchen islands' },
  { keys: ['bar cart'], cat: 'bar carts' },
  // Office
  { keys: ['desk', 'writing desk', 'executive desk', 'standing desk'], cat: 'desks' },
  { keys: ['office chair', 'task chair', 'ergonomic chair'], cat: 'office chairs' },
  { keys: ['bookcase (office)'], cat: 'bookcases (office)' },
  { keys: ['file cabinet', 'filing cabinet'], cat: 'filing cabinets' },
  { keys: ['hutch'], cat: 'hutches' },
  // Outdoor
  { keys: ['patio set', 'outdoor set', 'conversation set'], cat: 'patio sets' },
  { keys: ['outdoor sofa', 'patio sofa'], cat: 'outdoor sofas' },
  { keys: ['outdoor chair', 'patio chair'], cat: 'outdoor chairs' },
  { keys: ['outdoor table', 'patio table'], cat: 'outdoor tables' },
  { keys: ['lounger', 'chaise lounge (outdoor)'], cat: 'outdoor loungers' },
  // Decor & Other
  { keys: ['rug'], cat: 'rugs' },
  { keys: ['lamp', 'lighting', 'floor lamp', 'table lamp', 'chandelier'], cat: 'lighting' },
  { keys: ['mirror'], cat: 'mirrors' },
  { keys: ['storage', 'storage cabinet', 'storage bench'], cat: 'storage' },
  { keys: ['room divider', 'screen'], cat: 'room dividers' },
  { keys: ['entryway', 'hall tree'], cat: 'entryway furniture' },
  { keys: ['accent cabinet'], cat: 'accent cabinets' },
  { keys: ['shoe rack', 'shoe cabinet'], cat: 'shoe racks' },
  { keys: ['coat rack'], cat: 'coat racks' }
];

const toText = (detectedProduct = {}) => {
  const { name = '', description = '', brand = '', model = '', category = '' } = detectedProduct;
  return [name, description, brand, model, category].filter(Boolean).join(' ').toLowerCase();
};

export function suggestFurnitureCategory(detectedProduct = {}) {
  const text = toText(detectedProduct);
  if (!text) return { category: 'other', confidence: 0 };

  for (const entry of KEYWORD_MAP) {
    for (const k of entry.keys) {
      if (text.includes(k)) {
        return { category: entry.cat, confidence: 0.9 };
      }
    }
  }

  // fallback normalization if backend sends close match
  const normalized = normalizeCategory(detectedProduct.category);
  return { category: normalized, confidence: normalized === 'other' ? 0.2 : 0.6 };
}

export function normalizeCategory(raw) {
  const c = String(raw || '').trim().toLowerCase();
  if (!c) return 'other';

  // direct hit
  if (FURNITURE_CATEGORIES.includes(c)) return c;

  // simple plural/singular/spacing normalization
  const variants = [
    c,
    c.replace(/s$/, ''),
    c.endsWith('es') ? c.slice(0, -2) : c,
    c.replace(/-/g, ' '),
  ];

  for (const v of variants) {
    const match = FURNITURE_CATEGORIES.find(cat => cat === v || cat.replace(/s$/, '') === v || cat.replace(/-/g, ' ') === v);
    if (match) return match;
  }

  // partial contains
  const partial = FURNITURE_CATEGORIES.find(cat => c.includes(cat.replace(/\s*\(.*\)/, '')));
  return partial || 'other';
}
